import AntIcon from "@/components/icons/AntIcon";
import { List } from "@/components/list/List";
import { formatLocaleDate } from "@/utils/formatUtils";
import { tInterval } from "@/utils/translationUtils";
import { cssVariables, numberOrZero } from "@/utils/utils";
import { Avatar, Button, Flex, Tabs, TabsProps, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetDashboardAgentExpertises } from "../../../../../common/api/queries";
import { AgentCompetenceType } from "../../../../agent/enums";
import { AgentMentoring } from "../../../../agent/types";
import { formatAgentAggregatedName } from "../../../../agent/utils";
import { USER_PROFILE_TABS } from "../../../../globalsearch/utils";
import { CURRENT_USER_ROUTE_PATHS } from "../../../../user/paths";
import { AgentCompetenceDashboard } from "../../../types";
import { AgentCompetenceLevelTag } from "./components/AgentCompetenceLevelTag";
import { AgentCompetenceRemainingDaysPopover } from "./components/AgentCompetenceRemainingDaysPopover";
import { CompetencesTypeAvatar } from "./components/CompetencesTypeAvatar";
import { ExpertiseListItem } from "./components/ExpertiseListItem";

type AgentMentoringExtended = AgentMentoring & {
  isMentor: boolean;
};

enum EXPERTISE_TABS {
  COMPETENCES = "competences",
  MENTORING = "mentoring",
}

const getMoreInfoLink = (currentTabKey: EXPERTISE_TABS) => {
  switch (currentTabKey) {
    case EXPERTISE_TABS.COMPETENCES:
      return `${CURRENT_USER_ROUTE_PATHS.profile.to}?tab=${USER_PROFILE_TABS.COMPETENCES}`;
    case EXPERTISE_TABS.MENTORING:
      return `${CURRENT_USER_ROUTE_PATHS.profile.to}?tab=${USER_PROFILE_TABS.MENTORING}`;
    default:
      return "";
  }
};

export const DashboardAgentExpertisesWidget = () => {
  const { t } = useTranslation();

  const [selectedTabKey, setSelectedTabKey] = useState(EXPERTISE_TABS.COMPETENCES);

  const { data, isFetching } = useGetDashboardAgentExpertises();

  const currentTabKey = data && !data.competenceIntervals ? EXPERTISE_TABS.MENTORING : selectedTabKey;

  const getCompetenceLabel = (competence: AgentCompetenceDashboard) => {
    if (competence.type === AgentCompetenceType.OTHER) {
      return competence.label;
    }

    return (
      <>
        <span style={{ marginRight: 8 }}>{t(`agent.enums.financialSector.${competence.sector}`)}</span>
        {competence.level ? <AgentCompetenceLevelTag level={competence.level} /> : undefined}
      </>
    );
  };

  const getMentoringLabel = (mentoring: AgentMentoringExtended) => {
    if (mentoring.isMentor) {
      return (
        <>
          <span className="margin-right-tiny">
            {t("dashboard.agentExpertises.mentoring.adept")}: {formatAgentAggregatedName(mentoring.adept)}
          </span>

          <Tag color="geekblue" variant="filled">
            {t("dashboard.agentExpertises.mentoring.isMentor")}
          </Tag>
        </>
      );
    }

    return (
      <>
        <span className="margin-right-tiny">
          {t("dashboard.agentExpertises.mentoring.mentor")}: {formatAgentAggregatedName(mentoring.mentor)}
        </span>

        <Tag color="gold" variant="filled">
          {t("dashboard.agentExpertises.mentoring.isAdept")}
        </Tag>
      </>
    );
  };

  const getItems = () => {
    const items: TabsProps["items"] = [];

    if (data?.competenceIntervals) {
      items.push({
        key: "competences",
        label: t("dashboard.agentExpertises.competences.title"),
        children: (
          <List
            style={
              data.competenceIntervals.length === 0
                ? { backgroundColor: "white", borderRadius: 18, height: "100%" }
                : undefined
            }
            loading={isFetching}
            gap={16}
            showEmpty
            pagination={{
              pageSize: 3,
              size: "small",
              hideOnSinglePage: true,
            }}
            items={data.competenceIntervals}
            renderItem={(item) => {
              const remainingDays = Math.max(
                0,
                dayjs(item.endDate).startOf("day").diff(dayjs().startOf("day"), "days"),
              );

              const totalDays = dayjs(item.endDate).startOf("day").diff(dayjs(item.startDate).startOf("day"), "days");

              return (
                <ExpertiseListItem
                  totalDays={totalDays}
                  remainingDays={remainingDays}
                  label={getCompetenceLabel(item.competence)}
                  avatar={<CompetencesTypeAvatar type={item.competence.type} />}
                  popover={
                    <AgentCompetenceRemainingDaysPopover
                      remainingDays={remainingDays}
                      popoverContent={
                        <>
                          <div>
                            {`${t("dashboard.agentExpertises.helpers.validity")}: ${formatLocaleDate(item.startDate)} - ${formatLocaleDate(item.endDate)}`}
                          </div>

                          <div className="sub-header-info">
                            {t("dashboard.agentExpertises.helpers.remainingValidity")}:{" "}
                            {tInterval("dashboard.agentExpertises.helpers.remainingDays_interval", remainingDays)}
                          </div>
                        </>
                      }
                    />
                  }
                />
              );
            }}
          />
        ),
      });
    }

    const mentorings: AgentMentoringExtended[] = [
      ...(data?.mentoring?.mentoringsAsMentor.map((mentoring) => ({ ...mentoring, isMentor: true })) ?? []),
      ...(data?.mentoring?.mentoringsAsAdept.map((mentoring) => ({ ...mentoring, isMentor: false })) ?? []),
    ];

    if (data?.mentoring) {
      items.push({
        key: "mentoring",
        label: t("dashboard.agentExpertises.mentoring.title"),
        children: (
          <List
            loading={isFetching}
            style={mentorings.length === 0 ? { backgroundColor: "white", borderRadius: 18, height: "100%" } : undefined}
            gap={16}
            showEmpty
            pagination={{
              pageSize: 3,
              size: "small",
              hideOnSinglePage: true,
            }}
            items={mentorings}
            renderItem={(item) => {
              const remainingDays = Math.max(
                0,
                dayjs(item.endDate).startOf("day").diff(dayjs().startOf("day"), "days"),
              );

              const totalDays = dayjs(item.endDate).startOf("day").diff(dayjs(item.startDate).startOf("day"), "days");

              return (
                <ExpertiseListItem
                  label={getMentoringLabel(item)}
                  remainingDays={remainingDays}
                  totalDays={totalDays}
                  avatar={
                    <Tooltip title={t("dashboard.agentExpertises.mentoring.title")}>
                      <Avatar
                        style={{ backgroundColor: cssVariables.colorYellow }}
                        size="large"
                        shape="square"
                        icon={<AntIcon type="projection" />}
                      />
                    </Tooltip>
                  }
                  popover={
                    <AgentCompetenceRemainingDaysPopover
                      remainingDays={remainingDays}
                      popoverContent={
                        <>
                          <div>
                            {`${t("dashboard.agentExpertises.mentoring.from")} ${formatLocaleDate(item.startDate)} ${t("dashboard.agentExpertises.mentoring.to")} ${formatLocaleDate(item.endDate)}`}
                          </div>

                          <div>
                            {t("dashboard.agentExpertises.mentoring.missingContractsCount")}:{" "}
                            <b>
                              {Math.max(0, numberOrZero(data?.mentoring?.targetContractsCount) - item.contractsCount)}
                            </b>
                          </div>

                          <div className="sub-header-info">
                            {t("dashboard.agentExpertises.mentoring.untilEnd")}:{" "}
                            {tInterval("dashboard.agentExpertises.helpers.remainingDays_interval", remainingDays)}
                          </div>
                        </>
                      }
                    />
                  }
                />
              );
            }}
          />
        ),
      });
    }

    return items;
  };

  return (
    <Flex vertical style={{ height: "100%" }}>
      <Tabs
        activeKey={currentTabKey}
        onChange={(key) => setSelectedTabKey(key as EXPERTISE_TABS)}
        style={{ height: "100%" }}
        className="full-height-tabs"
        size="large"
        tabBarExtraContent={
          <Tooltip title={t("common.showMore")}>
            <Button
              type="text"
              size="large"
              href={getMoreInfoLink(currentTabKey)}
              target="_blank"
              icon={<AntIcon type="export" />}
            />
          </Tooltip>
        }
        items={getItems()}
      />
    </Flex>
  );
};
